import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, DollarSign, ToggleLeft, ToggleRight, History, Users } from 'lucide-react';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import Loading from '../components/common/Loading';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import PlanHistoryModal from '../components/PlanHistoryModal';
import { Plan } from '../types';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

const Plans: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('todos');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/plans');
      setPlans(response.data);
    } catch (err: any) {
      setError('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;

    setProcessing(true);
    try {
      await api.delete(`/plans/${selectedPlan.id}`);
      setSuccess('Plano excluído com sucesso');
      await loadPlans();
      setShowDeleteModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir plano');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (plan: Plan) => {
    try {
      await api.patch(`/plans/${plan.id}/toggle-status`);
      setSuccess('Status do plano alterado com sucesso');
      await loadPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao alterar status do plano');
    }
  };

  const filteredPlans = plans.filter((plan) => {
    const matchesSearch = plan.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'todos' || plan.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Planos</h1>
            <p className="text-gray-600">Gerencie os planos disponíveis para os associados</p>
          </div>
          <Button onClick={() => navigate('/planos/novo')}>
            <Plus className="w-5 h-5 mr-2" />
            Novo Plano
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="todos">Todos os Status</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError('')} className="mb-6">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} className="mb-6">
          {success}
        </Alert>
      )}

      {filteredPlans.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchTerm || statusFilter !== 'todos'
              ? 'Nenhum plano encontrado'
              : 'Nenhum plano cadastrado'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'todos'
              ? 'Tente ajustar os filtros de busca'
              : 'Comece cadastrando o primeiro plano'}
          </p>
          {!searchTerm && statusFilter === 'todos' && (
            <Button onClick={() => navigate('/planos/novo')}>
              <Plus className="w-5 h-5 mr-2" />
              Cadastrar Plano
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {plan.nome}
                    </h3>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(plan.valor)}
                      <span className="text-sm text-gray-500 font-normal">/mês</span>
                    </p>
                  </div>
                  <Badge variant={plan.status === 'ativo' ? 'success' : 'secondary'}>
                    {plan.status === 'ativo' ? 'Ativo' : 'Inativo'}
                  </Badge>
                  <Badge variant={plan.publish_plan === 1 ? 'success' : 'secondary'}>
                    {plan.publish_plan === 1 ? 'Publicado' : 'Não Publicado'}
                  </Badge>
                </div>

                {plan.descricao && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {plan.descricao}
                  </p>
                )}

                {plan.active_associates_count !== undefined && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                    <Users className="w-4 h-4" />
                    <span>
                      {plan.active_associates_count === 0
                        ? 'Nenhum associado vinculado'
                        : `${plan.active_associates_count} associado${plan.active_associates_count > 1 ? 's' : ''} ativo${plan.active_associates_count > 1 ? 's' : ''}`}
                    </span>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(`/planos/${plan.id}/editar`)}
                    className="flex-1"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setSelectedPlan(plan);
                      setShowHistoryModal(true);
                    }}
                    title="Ver histórico"
                  >
                    <History className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={plan.status === 'ativo' ? 'secondary' : 'primary'}
                    onClick={() => handleToggleStatus(plan)}
                    title={plan.status === 'ativo' ? 'Desativar' : 'Ativar'}
                  >
                    {plan.status === 'ativo' ? (
                      <ToggleRight className="w-5 h-5" />
                    ) : (
                      <ToggleLeft className="w-5 h-5" />
                    )}
                  </Button>
                  {plan.can_be_deleted && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        setSelectedPlan(plan);
                        setShowDeleteModal(true);
                      }}
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDeleteModal && selectedPlan && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Excluir Plano"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja excluir o plano{' '}
              <strong>{selectedPlan.nome}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta ação não poderá ser desfeita. O plano só poderá ser excluído se
              não houver associados ativos vinculados.
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={processing}
                disabled={processing}
                className="flex-1"
              >
                Confirmar Exclusão
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {selectedPlan && (
        <PlanHistoryModal
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedPlan(null);
          }}
          planId={selectedPlan.id}
          planName={selectedPlan.nome}
        />
      )}
    </div>
  );
};

export default Plans;
